import { supabase } from "@/integrations/supabase/client";

/**
 * Resolve an avatar reference to a displayable URL.
 *
 * Avatars are stored in a private bucket. New uploads store the storage *path*
 * (e.g. `<userId>/avatar.png`) in profiles.avatar_url and we exchange that for
 * a short-lived signed URL at display time.
 *
 * For backward compatibility we also accept:
 *  - External absolute URLs (Google OAuth `picture`, etc.) — returned as-is.
 *  - Legacy public Supabase URLs containing `/storage/v1/object/public/avatars/`
 *    — we extract the path and sign it.
 */

const SIGN_TTL_SECONDS = 60 * 60; // 1 hour

const cache = new Map<string, { url: string; expiresAt: number }>();

function extractStoragePath(value: string): string | null {
  // Already a bare storage path like "userId/avatar.png"
  if (!value.includes("://") && !value.startsWith("/")) {
    // Strip any cache-busting query string
    return value.split("?")[0];
  }

  // Legacy public URL pattern
  const publicMarker = "/storage/v1/object/public/avatars/";
  const publicIdx = value.indexOf(publicMarker);
  if (publicIdx !== -1) {
    return value.substring(publicIdx + publicMarker.length).split("?")[0];
  }

  // Already-signed URL pattern — extract path so we can re-sign on expiry
  const signedMarker = "/storage/v1/object/sign/avatars/";
  const signedIdx = value.indexOf(signedMarker);
  if (signedIdx !== -1) {
    return value.substring(signedIdx + signedMarker.length).split("?")[0];
  }

  return null;
}

export async function resolveAvatarUrl(value: string | null | undefined): Promise<string | null> {
  if (!value) return null;

  const path = extractStoragePath(value);

  // Not a Supabase storage reference (e.g. Google OAuth URL) — use as-is
  if (!path) return value;

  // Check cache
  const cached = cache.get(path);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url;
  }

  const { data, error } = await supabase.storage
    .from("avatars")
    .createSignedUrl(path, SIGN_TTL_SECONDS);

  if (error || !data?.signedUrl) {
    return null;
  }

  cache.set(path, {
    url: data.signedUrl,
    // Refresh slightly before expiry
    expiresAt: Date.now() + (SIGN_TTL_SECONDS - 60) * 1000,
  });

  return data.signedUrl;
}

export function clearAvatarCache(pathOrUrl?: string) {
  if (!pathOrUrl) {
    cache.clear();
    return;
  }
  const path = extractStoragePath(pathOrUrl);
  if (path) cache.delete(path);
}

export function getAvatarStoragePath(value: string | null | undefined): string | null {
  if (!value) return null;
  return extractStoragePath(value);
}
